import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Header } from "../../../components/SpearParts/Layouts/Header";
import { SeachSection } from "../../../components/Engineering/Layout/SeachSection";
import Axios from "../../../axios/axios";
import { Footer } from "../../../components/SpearParts/Layouts/Footer";
import { toTitleCase } from "../../../components/utils/StringUtil";
import NoData from "../../../components/NoData/Index";
import "../style.css";
import EngineeringAdvancedSearch from "./EngineeringAdvanceSearch";
import EngMiddle from "../../../components/Engineering/Layout/EngMiddle";
import TopNavBar from "../../../components/SpearParts/Layouts/TopNavBar";
import TopSearchBar from "../../../components/SpearParts/Layouts/TopSearchBar";

export default function EngProductList() {
  const location = useLocation();
  const {
    parts: rawParts = [],
    engineeringBrand = [],
    engineeringCategory = [],
    engineeringModels = [],
    region = [],
    subcategories = [],
  } = location.state || {};
  const parts = Array.isArray(rawParts) ? rawParts : [];
  const [originalSearchResults, setOriginalSearchResults] = useState(parts);

  const [filters, setFilters] = useState(
    location.state?.selectedFilters || {
      year: "",
      regionId: "",
      steering: "",
      bodyType: "",
      transmissionType: "",
      body: "",
      modification: "",
      engine: "",
      engineCode: "",
      grade: "",
      subcategoryId: "",
    }
  );
  const [advResults, setAdvResults] = useState([]);
  const getUniqueById = (arr) => {
    const seen = new Set();
    return arr.filter((item) => {
      if (seen.has(item._id)) return false;
      seen.add(item._id);
      return true;
    });
  };
  const uniqueAdvResults = getUniqueById(advResults);
  const [filteredParts, setFilteredParts] = useState(parts);

  // Extract unique values for dropdowns from parts data
  const extractUniqueValues = (key) => {
    if (!Array.isArray(parts)) return [];
    return [...new Set(parts.map((part) => part[key]).filter(Boolean))];
  };

  // Pagination states

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Total pages count

  const dropdownData = {
    years: extractUniqueValues("year"),
    regions: extractUniqueValues("regionId"),
    steerings: extractUniqueValues("steering"),
    bodyTypes: extractUniqueValues("bodyType"),
    transmissions: extractUniqueValues("transmissionType"),
    bodies: extractUniqueValues("body"),
    modifications: extractUniqueValues("modification"),
    engines: extractUniqueValues("engine"),
    engineCodes: extractUniqueValues("engineCode"),
    grades: extractUniqueValues("grade"),
  };

  const getBrandName = (brandId) => {
    if (!brandId) return "Unknown Brand";
    // brandId may be a populated object ({_id, name}) or a string id
    return (
      brandId?.name ||
      engineeringBrand.find((b) => b._id === (brandId._id || brandId))?.name ||
      "Unknown Brand"
    );
  };
  const getManufacturerName = (manufacturer) => {
    if (!manufacturer) return "Unknown Manufacturer";
    // manufacturer can be populated object or id; if object, return its name
    return manufacturer?.manufacturerName || "Unknown Manufacturer";
  };

  const getModelName = (modelId) =>
    engineeringModels.find((m) => m._id === modelId)?.name || "Unknown Model";

  const getCategoryName = (category) => category?.name || "Unknown Category";

  const getRegionName = (regionId) =>
    region.find((m) => m._id === regionId)?.name || "Unknown Region";

  const handleFilterChange = (updatedFilters) => {
    const finalFilters = { ...filters, ...updatedFilters, page: 1 };

    setFilters(finalFilters);

    Axios.get("/engparts/searchCombined", { params: finalFilters }).then(
      (response) => {
        setFilteredParts(response.data.data);
        setOriginalSearchResults(response.data.data);
      }
    );
  };

  // If navigation provided selectedFilters in state, keep them in filters
  useEffect(() => {
    if (location.state?.selectedFilters) {
      setFilters(location.state.selectedFilters);
    }
  }, [location.state]);

  // Always fetch fresh data from server when this page loads or when URL search changes.
  // This ensures: when user clicks a category card — product list calls backend with that categoryId + old filters.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlFilters = {
      categoryId: searchParams.get("categoryId"),
      subcategoryId: searchParams.get("subcategoryId"),
      brandId: searchParams.get("brandId"),
      manufacturerId: searchParams.get("manufacturerId"),
      // keep other params if needed in future
    };

    const hasAnyFilter = !!(
      urlFilters.categoryId ||
      urlFilters.brandId ||
      urlFilters.manufacturerId
    );

    const fetchParts = async () => {
      try {
        if (hasAnyFilter) {
          // Call engineering search API (same endpoint your searchPart util uses)
          const res = await Axios.get("/engparts/searchCombined", {
            params: { ...urlFilters, ...filters, page: currentPage, limit: 15 },
          });
          setFilteredParts(res.data.data || []);
          setOriginalSearchResults(res.data.data || []);
          setTotalPages(res.data.totalPages || 1);
        } else {
          // No filters in URL — fallback: if location.state.parts present use that, else empty
          if (location.state?.parts) {
            setFilteredParts(location.state.parts);
          } else {
            setFilteredParts([]);
          }
        }
        setCurrentPage(1);
      } catch (error) {
        console.error(
          "Error fetching parts from /engparts/searchCombined:",
          error
        );
        // fallback to location.state.parts if available
        if (location.state?.parts) {
          setFilteredParts(location.state.parts);
        } else {
          setFilteredParts([]);
        }
      }
    };

    fetchParts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Keep previous behavior: if state contains parts, set them (useful for direct navigation without reload)
  useEffect(() => {
    if (location.state?.parts) {
      setFilteredParts(location.state.parts);
      setFilters(location.state.selectedFilters || filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {}, [filteredParts]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlFilters = {
      categoryId: searchParams.get("categoryId"),
      subcategoryId: searchParams.get("subcategoryId") || "",
      brandId: searchParams.get("brandId"),
      manufacturerId: searchParams.get("manufacturerId"),
    };

    Axios.get("/engparts/searchCombined", {
      params: { ...urlFilters, ...filters, page: currentPage, limit: 15 },
    })
      .then((res) => {
        setFilteredParts(res.data.data || []);
        setOriginalSearchResults(res.data.data || []);
        setTotalPages(res.data.totalPages || 1);
      })
      .catch(() => setFilteredParts([]));
  }, [currentPage]);

  // Card component for individual parts
  const PartCard = ({ product }) => (
    <div className="col-lg-4 col-md-6 col-sm-12 mb-4">
      <Link
        to={`/engineering/product/single/${product._id}`}
        style={{ textDecoration: "none", color: "inherit" }}
      >
        <div
          className="prod-div p-2 d-flex flex-column align-items-center"
          style={{
            border: "1px solid #ddd",
            borderRadius: "8px",
            height: "100%",
          }}
        >
          {product?.images?.length > 0 && (
            <img
              src={product.images[0].url}
              alt={product.partName}
              style={{
                width: "100%",
                height: "180px",
                objectFit: "contain",
                marginBottom: "60px",
              }}
            />
          )}

          <h6 className="mb-2 text-center">{toTitleCase(product.partName)}</h6>
        </div>
      </Link>
    </div>
  );
  const sectionStyle = {
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundImage: "url(/images/bnr-bg.png)",
  };
  const beforeImageStyle = {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    width: "360px",
    height: "100%",
    backgroundImage: "url(/images/eng-head-image.png)",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "left top",
    backgroundSize: "contain",
    pointerEvents: "none",
    zIndex: 1,
  };

  return (
    <>
      <div className="New-Eng-Dashboard" style={sectionStyle}>
        {/* <Header /> */}
        <div className="before-image" style={beforeImageStyle}></div>
        <div className="Main-div">
          <TopNavBar />
          <div className="TopSearchBar">
            <TopSearchBar />
          </div>
          <div className="container">
            <div className="mb-4">
              <EngMiddle
                initialFilters={filters}
                engineeringBrand={engineeringBrand}
                engineeringCategory={engineeringCategory}
                engineeringModels={engineeringModels}
                region={region}
                years={dropdownData.years}
                onFilterChange={handleFilterChange}
              />
            </div>

            <div
              className="BuildingProductList"
              // style={{
              //   width: "90%",
              //   alignContent: "center",
              //   margin: "auto",
              //   display: "flex",
              // }}
            >
              <div className="">
                <div className="advanced-filter-card p-3 mb-50">
                  <EngineeringAdvancedSearch
                    filters={filters}
                    categories={engineeringCategory}
                    subcategories={subcategories}
                    setFilters={(updatedField) => {
                      handleFilterChange(updatedField);
                    }}
                  />
                </div>
              </div>

              <div className="col-lg-9">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h6 className="m-0">
                    Search results ({filteredParts.length})
                  </h6>

                  <div className="d-flex gap-2">
                    {/* ALL */}
                    <button
                      className="btn btn-sm btn-outline-dark"
                      onClick={() => {
                        const newFilters = {
                          ...filters,
                          partQuality: "",
                          page: 1,
                        };
                        setFilters(newFilters);
                        Axios.get("/engparts/searchCombined", {
                          params: newFilters,
                        }).then((res) => {
                          setFilteredParts(res.data.data);
                          setOriginalSearchResults(res.data.data);
                          setTotalPages(res.data.totalPages);
                          setCurrentPage(1);
                        });
                      }}
                    >
                      All
                    </button>

                    {/* GENUINE */}
                    <button
                      className="btn btn-sm btn-outline-success"
                      onClick={() => {
                        const newFilters = {
                          ...filters,
                          partQuality: "genuine",
                          page: 1,
                        };
                        setFilters(newFilters);
                        Axios.get("/engparts/searchCombined", {
                          params: newFilters,
                        }).then((res) => {
                          setFilteredParts(res.data.data);
                          setOriginalSearchResults(res.data.data);
                          setTotalPages(res.data.totalPages);
                          setCurrentPage(1);
                        });
                      }}
                    >
                      Genuine
                    </button>

                    {/* AFTERMARKET */}
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        const newFilters = {
                          ...filters,
                          partQuality: "aftermarket",
                          page: 1,
                        };
                        setFilters(newFilters);
                        Axios.get("/engparts/searchCombined", {
                          params: newFilters,
                        }).then((res) => {
                          setFilteredParts(res.data.data);
                          setOriginalSearchResults(res.data.data);
                          setTotalPages(res.data.totalPages);
                          setCurrentPage(1);
                        });
                      }}
                    >
                      Aftermarket
                    </button>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table search-table">
                    <tbody>
                      <tr className="force-bg-red">
                        <td colSpan="8" className="border-td p-0">
                          <div className="product-header-row">
                            <div className="head-cell name">Name</div>
                            <div className="head-cell">Part No</div>
                            <div className="head-cell">Category</div>
                            <div className="head-cell">Model</div>
                            <div className="head-cell">Brand</div>
                            <div className="head-cell">Manufacturer</div>
                            <div className="head-cell">Year</div>
                            <div className="head-cell icon"></div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="8" className="border-td p-0">
                          <table className="table search-inner-table">
                            <tbody>
                              {filteredParts.length > 0 ? (
                                filteredParts.map((part, index) => (
                                  <tr
                                    key={index}
                                    className="product-row-card p-2"
                                  >
                                    <td colSpan="8" className="p-1">
                                      <div className="product-row">
                                        <div className="cell name">
                                          <Link
                                            to={`/engineering/product/single/${part._id}`}
                                          >
                                            {part.partName}
                                          </Link>
                                        </div>

                                        <div className="cell">
                                          {part.partNo || "N/A"}
                                        </div>

                                        <div className="cell">
                                          {getCategoryName(part.categoryId)}
                                        </div>

                                        <div className="cell">
                                          {getModelName(part.modelId)}
                                        </div>

                                        <div className="cell">
                                          {toTitleCase(
                                            getBrandName(part.brandId)
                                          )}
                                        </div>

                                        <div className="cell">
                                          {toTitleCase(
                                            getManufacturerName(
                                              part.manufacturer
                                            )
                                          )}
                                        </div>

                                        <div className="cell">{part.year}</div>

                                        <div className="cell icon">
                                          <Link
                                            to={`/engineering/product/single/${part._id}`}
                                          >
                                            <img src="/images/go.png" alt="" />
                                          </Link>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td
                                    colSpan="8"
                                    className="text-center"
                                    justifyContent="center"
                                  >
                                    <NoData />
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="pagination-container d-flex justify-content-end align-items-center mt-4">
                    <button
                      className="pagination-btn"
                      disabled={currentPage === 1}
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                    >
                      <i className="fa fa-arrow-left"></i>
                    </button>

                    <span className="mx-3">
                      {currentPage} of {totalPages}
                    </span>

                    <button
                      className="pagination-btn"
                      disabled={currentPage === totalPages}
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                    >
                      <i className="fa fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* <Footer /> */}
      </div>
    </>
  );
}
